import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { HRService } from '@/services/hr.service';
import { TimeOffRequest, TimeOffType, TimeOffStatus } from '@/models/hr.model';
import { useAuth } from '@/contexts/AuthContext';
import { Button, FormInput, FormSelect, FormTextarea, LoadingSpinner, Card } from '@/components/common';
import { Calendar, User, CalendarClock, FileText, ArrowLeft, Save } from 'lucide-react';

export const TimeOffForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const isEditing = !!id;
  const employeeId = searchParams.get('employeeId') || profile?.id || '';

  const [formData, setFormData] = useState<Partial<TimeOffRequest>>({
    employeeId,
    type: TimeOffType.vacation,
    startDate: new Date(),
    endDate: new Date(),
    days: 1,
    status: TimeOffStatus.pending,
    reason: '',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: timeOff, isLoading } = useQuery({
    queryKey: ['timeOff', id],
    queryFn: () => id ? HRService.getAllTimeOffRequests().then(requests => requests.find(r => r.id === id)) : null,
    enabled: isEditing && !!id,
  });

  const { data: employee } = useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => employeeId ? HRService.getEmployeeById(employeeId) : null,
    enabled: !!employeeId,
  });

  React.useEffect(() => {
    if (timeOff) {
      setFormData(timeOff);
    } else if (employee) {
      setFormData(prev => ({
        ...prev,
        employeeId: employee.id,
        employeeName: employee.fullName,
      }));
    }
  }, [timeOff, employee]);

  const calculateDays = (start: Date, end: Date) => {
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const mutation = useMutation({
    mutationFn: async (data: Partial<TimeOffRequest>) => {
      if (isEditing && id) {
        await HRService.updateTimeOffRequest(id, data);
      } else {
        await HRService.createTimeOffRequest(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeOffRequests'] });
      navigate('/hr');
    },
    onError: (error: any) => {
      setErrorMessage(error?.message || 'Failed to save time off request');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!formData.employeeId || !formData.startDate || !formData.endDate) {
      setErrorMessage('Employee, Start Date, and End Date are required');
      return;
    }

    const days = calculateDays(formData.startDate, formData.endDate);
    mutation.mutate({ ...formData, days });
  };

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSpinner text="Loading time off request..." />
      </div>
    );
  }

  const typeOptions = Object.values(TimeOffType).map(type => ({
    value: type,
    label: type.charAt(0).toUpperCase() + type.slice(1),
  }));

  const statusOptions = Object.values(TimeOffStatus).map(status => ({
    value: status,
    label: status.charAt(0).toUpperCase() + status.slice(1),
  }));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-center gap-4">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/hr')}
        >
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-8 h-8 text-green-600" />
            {isEditing ? 'Edit Time Off Request' : 'New Time Off Request'}
          </h1>
          <p className="mt-1 text-gray-600">
            {isEditing ? 'Update time off request details' : 'Submit a new time off request'}
          </p>
        </div>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {errorMessage && (
            <div className="bg-red-50 border-l-4 border-red-400 rounded-r-lg p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-semibold text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{errorMessage}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="Employee"
              icon={User}
              value={employee?.fullName || formData.employeeName || ''}
              disabled
              className="bg-gray-50"
            />

            <FormSelect
              label="Type"
              icon={CalendarClock}
              required
              options={typeOptions}
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as TimeOffType })}
            />

            <FormInput
              label="Start Date"
              type="date"
              icon={Calendar}
              required
              value={formData.startDate ? new Date(formData.startDate).toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const start = new Date(e.target.value);
                setFormData({
                  ...formData,
                  startDate: start,
                  days: formData.endDate ? calculateDays(start, formData.endDate) : 1,
                });
              }}
            />

            <FormInput
              label="End Date"
              type="date"
              icon={Calendar}
              required
              value={formData.endDate ? new Date(formData.endDate).toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const end = new Date(e.target.value);
                setFormData({
                  ...formData,
                  endDate: end,
                  days: formData.startDate ? calculateDays(formData.startDate, end) : 1,
                });
              }}
            />

            <FormInput
              label="Days"
              type="number"
              icon={CalendarClock}
              value={formData.days || 0}
              disabled
              className="bg-gray-50"
              helperText="Automatically calculated from start and end dates"
            />

            {!isEditing && (
              <FormSelect
                label="Status"
                options={statusOptions}
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as TimeOffStatus })}
              />
            )}
          </div>

          <FormTextarea
            label="Reason"
            icon={FileText}
            rows={3}
            value={formData.reason || ''}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            placeholder="Provide a reason for this time off request..."
          />

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => navigate('/hr')}
              icon={ArrowLeft}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="success"
              icon={Save}
              isLoading={mutation.isPending}
              iconPosition="right"
            >
              {isEditing ? 'Update Request' : 'Submit Request'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

